// §3.3 / §3.4 / §7.2 canonical inline MASL assembly.
import type { BlobRef, BuildMeta, BuiltBlob, MaslDocument, MaslIcon, MaslResourceEntry, Routing } from "./types.ts";
import { BuildError } from "./normalize.ts";

function blobRef(cid: string, size: number, mimeType: string): BlobRef {
  // §7.2: mimeType MUST equal the resource's true media type as a conforming PDS stores it (the
  // byte-sniffed type), so the record's blob ref agrees with the uploaded blob and `applyWrites` is
  // accepted. The serving type lives in the entry's `content-type` (usually the same string).
  return { $type: "blob", ref: { $link: cid }, mimeType, size };
}

export interface AssembleInput {
  meta: BuildMeta;
  blobs: BuiltBlob[];
  entryPath: string;
  icons: MaslIcon[];
  routing: Routing | undefined;
}

/**
 * Assemble the canonical MASL. Top level is exactly `{ name, resources, <optional display + routing
 * when set> }`; each entry is `{ src, "content-type", <optional csp/sourcemap> }`. A `/` entry
 * aliasing the entry document and a non-empty `name` are required. Optional fields are omitted when
 * unset (never null). Encoding/key order is handled by the dCBOR42 encoder (§3.7).
 */
export function assembleMasl(input: AssembleInput): MaslDocument {
  const { meta, blobs, entryPath, icons, routing } = input;
  if (!meta.name || meta.name.length === 0) throw new BuildError(`meta.name is required (§3.3)`);

  const byPath = new Map(blobs.map((b) => [b.path, b]));
  const entry = byPath.get(entryPath);
  if (entry === undefined) throw new BuildError(`entry ${JSON.stringify(entryPath)} missing from blobs`);

  const resources: Record<string, MaslResourceEntry> = {};
  // `/` alias first (naming the root document, §3.3).
  resources["/"] = { src: blobRef(entry.cid, entry.size, entry.blobMimeType), "content-type": entry.contentType };
  for (const b of blobs) {
    resources[b.path] = { src: blobRef(b.cid, b.size, b.blobMimeType), "content-type": b.contentType };
  }

  const resourcePaths = new Set(Object.keys(resources));

  // Validate icons reference in-map paths (§3.3).
  for (const ic of icons) {
    if (!resourcePaths.has(ic.src)) {
      throw new BuildError(`icon src ${JSON.stringify(ic.src)} is not present in resources (§3.3)`);
    }
  }
  // Validate any sourcemap references stay in-map (§3.4). (No synthesis path sets sourcemap yet.)
  for (const [path, entryObj] of Object.entries(resources)) {
    if (entryObj.sourcemap !== undefined && !resourcePaths.has(entryObj.sourcemap)) {
      throw new BuildError(`sourcemap ${JSON.stringify(entryObj.sourcemap)} on ${path} is not in resources (§3.4)`);
    }
  }

  const doc: MaslDocument = { name: meta.name, resources };
  if (meta.short_name !== undefined) doc.short_name = meta.short_name;
  if (meta.theme_color !== undefined) doc.theme_color = meta.theme_color;
  if (meta.background_color !== undefined) doc.background_color = meta.background_color;
  if (meta.description !== undefined) doc.description = meta.description;
  if (meta.categories !== undefined) doc.categories = meta.categories;
  if (icons.length > 0) doc.icons = icons;
  if (routing !== undefined) doc.routing = routing;

  return doc;
}
